import { instanceKey } from "@/lib/instanceKey";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type { DbtJobHistory } from "@shared/types/dbtJobHistory";
import type {
  CreateDbtJobBody,
  RunDbtJobsBody,
  StartTransformRunResponse,
  TransformationGraph,
  TransformationNodeSource,
  DbtJobRunRow,
  DbtJobView,
  UpdateDbtJobBody,
} from "@shared/types/transformations";
import {
  ApiClientError,
  DBT_JOBS_GRAPH_PATH,
  DBT_JOBS_PATH,
  DBT_JOBS_RUNS_PATH,
  apiRequest,
  apiRequestEnvelope,
  createDbtJob,
  deleteDbtJob,
  listDbtJobRuns,
  startTransformRun,
  dbtJobHistoryPath,
  dbtJobPath,
  dbtJobRunPath,
  updateDbtJob,
} from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";
import { RUNS_PATH } from "@/hooks/use-runs";

/**
 * dbt jobs (FR-62): the list the schedule editor's picker and the
 * Transformations screen share, one dbt job, the graph and the recent
 * transform steps the screen reads, and the writes behind the drawer and the
 * "Run all dbt jobs now" button.
 *
 * `queryKey[0]` is the literal request path, as everywhere else in the app, so
 * one path is one cache entry. The recent-steps key carries its limit as a
 * second element so "Load more" (a larger limit) is a second entry that does
 * not blank the first, and so invalidating `[DBT_JOBS_RUNS_PATH]` reaches
 * every limit by prefix.
 *
 * The list's 404 handling predates this screen: the route is owned by another
 * package and may not be mounted in every deployment of this branch, so a 404
 * is "this deployment has no dbt jobs API" and resolves to `null` rather
 * than throwing. `null` hides the schedule editor's section with a one-line
 * explanation; an empty array means the API answered and this instance simply
 * has no models yet. Collapsing the two would make a missing route look like a
 * configured-and-empty instance, which is the harder bug to notice.
 */

export { DBT_JOBS_GRAPH_PATH, DBT_JOBS_PATH, DBT_JOBS_RUNS_PATH };
export { dbtJobPath };

/**
 * The key the detail query parks on before it has an id. A disabled query
 * still READS its key, so a fallback to the list's key would hand the detail
 * hook the whole array typed as one row (see `SCHEDULE_DISABLED_KEY`).
 */
export const DBT_JOB_DISABLED_KEY = "transformation-detail-disabled";

/** The page sizes "Load more" walks through; the route's maximum is 100. */
export const DBT_JOB_RUNS_PAGE_SIZES = [20, 50, 100] as const;

export type DbtJobsResult = DbtJobView[] | null;

export function useDbtJobs(enabled = true): UseQueryResult<DbtJobsResult> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<DbtJobsResult>({
    ...listQueryOptions,
    queryKey: instanceKey(DBT_JOBS_PATH, selectedInstance?.id),
    enabled,
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest<DbtJobView[]>(DBT_JOBS_PATH, { signal });
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

/** One dbt job, or nothing until the route has an id. */
export function useDbtJob(id: string | null): UseQueryResult<DbtJobView> {
  return useQuery<DbtJobView>({
    queryKey: [id ? dbtJobPath(id) : DBT_JOB_DISABLED_KEY],
    enabled: Boolean(id),
  });
}

/**
 * The last launched run's model graph (API-STANDARD.md §7b). It reads three
 * blobs on the server, so the default 30 s staleness is kept and every write
 * that can change it invalidates it explicitly.
 */
export function useTransformationGraph(enabled = true): UseQueryResult<TransformationGraph> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<TransformationGraph>({
    queryKey: instanceKey(DBT_JOBS_GRAPH_PATH, selectedInstance?.id),
    enabled,
  });
}

export interface DbtJobRunsPage {
  rows: DbtJobRunRow[];
  pageSize: number;
}

/** The transform step of the most recent `limit` runs, newest first. */
export function useDbtJobRuns(limit: number, enabled = true): UseQueryResult<DbtJobRunsPage> {
  return useQuery<DbtJobRunsPage>({
    ...listQueryOptions,
    queryKey: [DBT_JOBS_RUNS_PATH, limit],
    enabled,
    queryFn: ({ signal }) => listDbtJobRuns(limit, signal),
    // A step in flight changes every poll; the header follows it.
    staleTime: 0,
  });
}

/**
 * #841: re-read every mounted `useDbtJobRuns(limit)` entry, whatever its limit. The landing reads
 * a page of five while the Models and Tests views read one row each, and the landing's poll must
 * keep theirs fresh too (Ruling 18, F2a). The History view's paged entry (`"pages"` keys) is left
 * to its own first-page refresh.
 */
export function refetchNewestRuns(client: QueryClient): Promise<void> {
  return client.refetchQueries({
    type: "active",
    predicate: (query) =>
      query.queryKey[0] === DBT_JOBS_RUNS_PATH && typeof query.queryKey[1] === "number",
  });
}

/** Round 7: the runs pages the History view walks, `DBT_JOB_RUNS_PAGE_SIZES[0]` at a time. */
export interface DbtJobRunsCursorPage {
  rows: DbtJobRunRow[];
  nextCursor: string | null;
}

/**
 * Round 7 ("dbt job runs cursor past 100"): the transform steps, newest first, a page at a time
 * by the server's keyset cursor, so Load more pages past 100 rather than widening one read. The
 * key sits under `[DBT_JOBS_RUNS_PATH]`, so every write that invalidates the runs reaches
 * it, and that invalidation re-reads every loaded page. The poll while the newest is in flight
 * re-reads the first page only (`useRefreshFirstRunPage`).
 */
export function useDbtJobRunPages(
  pageSize: number,
): UseInfiniteQueryResult<{ pages: DbtJobRunsCursorPage[]; pageParams: (string | null)[] }, Error> {
  return useInfiniteQuery<
    DbtJobRunsCursorPage,
    Error,
    { pages: DbtJobRunsCursorPage[]; pageParams: (string | null)[] },
    [string, string, number],
    string | null
  >({
    ...listQueryOptions,
    queryKey: [DBT_JOBS_RUNS_PATH, "pages", pageSize],
    initialPageParam: null,
    queryFn: async ({ pageParam, signal }) => {
      const page = await listDbtJobRuns(pageSize, signal, pageParam);
      return { rows: page.rows, nextCursor: page.nextCursor };
    },
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 0,
    // A failed next page shows inline beside Load more, which asks for it again.
    retry: false,
  });
}

type DbtJobRunPages = {
  pages: DbtJobRunsCursorPage[];
  pageParams: (string | null)[];
};

/**
 * Review r2 (round 7): the loaded pages with a fresh first page in place of the old one. The
 * fresh page leads; a row of the old first page that a newer run pushed off it stays, after the
 * fresh rows (it is older than all of them), so no row drops out between the first page and the
 * next one's cursor. The old first page's cursor is kept: the next page starts where it did.
 */
function mergeFirstRunPage(data: DbtJobRunPages, fresh: DbtJobRunsCursorPage): DbtJobRunPages {
  const [first, ...rest] = data.pages;
  if (!first) return { ...data, pages: [fresh] };
  const freshIds = new Set(fresh.rows.map((row) => row.runId));
  const pushed = first.rows.filter((row) => !freshIds.has(row.runId));
  // With one page loaded there is no next page to keep in step with: take the fresh page whole.
  if (rest.length === 0) return { ...data, pages: [fresh] };
  return {
    ...data,
    pages: [{ rows: [...fresh.rows, ...pushed], nextCursor: first.nextCursor }, ...rest],
  };
}

/**
 * Review r2 (round 7): the in-flight poll re-reads the FIRST page only. `refetch()` on an
 * infinite query re-reads every loaded page one after another, so after six Load more clicks a
 * 5 s poll made seven requests; this makes one, and keeps the later pages as they were read.
 *
 * Lead round: one read at a time. A call while a read is pending returns that read, so a slow
 * answer is never overtaken by a later poll and merged after it (it would push the newest run
 * below older ones). An answer is dropped when the query was re-read meanwhile (a write's
 * invalidation is newer), and the read is aborted on unmount.
 */
export function useRefreshFirstRunPage(pageSize: number): () => Promise<void> {
  const queryClient = useQueryClient();
  const pending = useRef<Promise<void> | null>(null);
  const aborter = useRef<AbortController | null>(null);
  useEffect(() => () => aborter.current?.abort(), []);
  return useCallback(() => {
    if (pending.current) return pending.current;
    const key = [DBT_JOBS_RUNS_PATH, "pages", pageSize];
    const controller = new AbortController();
    aborter.current = controller;
    const readAt = queryClient.getQueryState(key)?.dataUpdatedAt;
    const read = (async () => {
      const fresh = await listDbtJobRuns(pageSize, controller.signal, null);
      if (queryClient.getQueryState(key)?.dataUpdatedAt !== readAt) return;
      queryClient.setQueryData<DbtJobRunPages>(key, (data) =>
        data ? mergeFirstRunPage(data, { rows: fresh.rows, nextCursor: fresh.nextCursor }) : data,
      );
    })().finally(() => {
      pending.current = null;
    });
    pending.current = read;
    return read;
  }, [queryClient, pageSize]);
}

/** Round 7: one run's row whole, only while `runId` is set (a shortened row's details). */
export function useDbtJobRun(runId: string | null): UseQueryResult<DbtJobRunRow> {
  return useQuery<DbtJobRunRow>({
    queryKey: [runId ? dbtJobRunPath(runId) : DBT_JOB_RUN_DISABLED_KEY],
    enabled: Boolean(runId),
  });
}

/** The key the one-run query parks on while no shortened row is open. */
export const DBT_JOB_RUN_DISABLED_KEY = "transformation-run-disabled";

/** The audited source SQL read of one dbt node in one run (API-STANDARD §7b). */
export function transformationNodeSourcePath(runId: string, nodeId: string): string {
  return `${DBT_JOBS_RUNS_PATH}/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeId)}/source`;
}

/** The key the source query parks on while no dialog is open. */
export const TRANSFORMATION_NODE_SOURCE_DISABLED_KEY = "transformation-node-source-disabled";

/**
 * The source SQL of one model or test in one run, as written in the repository, for the Query
 * dialog (compiled SQL is never served: ADR 0011 amendment 2026-10-14). The read is AUDITED on
 * the server, so it is made once per open of the dialog and never again while it is open: no
 * retry, no refetch on focus or reconnect, never stale while mounted. The text is kept only in
 * the open dialog's query: `gcTime: 0` drops it the moment the dialog closes, so the next open is
 * a new read (and a new audit row), never a cached copy.
 */
export function useTransformationNodeSource(
  target: { runId: string; nodeId: string } | null,
): UseQueryResult<TransformationNodeSource> {
  return useQuery<TransformationNodeSource>({
    queryKey: [
      target
        ? transformationNodeSourcePath(target.runId, target.nodeId)
        : TRANSFORMATION_NODE_SOURCE_DISABLED_KEY,
    ],
    enabled: Boolean(target),
    gcTime: 0,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** The history's cache key: the path, then `include`, so the upstream toggle is its own entry. */
export function dbtJobHistoryKey(
  id: string,
  include: "dependencies" | undefined,
): [string, string] {
  return [`${dbtJobPath(id)}/history`, include ?? "members"];
}

/** Runs per history page: the columns a screen shows before "Load older runs". */
export const DBT_JOB_HISTORY_PAGE_SIZE = 10;

/** One page of the history, as the timeline merges it. */
export interface DbtJobHistoryPage {
  data: DbtJobHistory;
  nextCursor: string | null;
}

/**
 * One transformation's per-model history (session 7, canvas 18), a page of
 * runs at a time. LAZY: `enabled` is true only while the History tab is open,
 * so every other detail test (and every other tab) makes no extra request.
 * `retry` is off for the same reason as the run history: the error shows
 * inline with a "Try again", and older pages already fetched stay on screen.
 */
export function useDbtJobHistory(
  id: string,
  options: { enabled: boolean; include?: "dependencies" },
): UseInfiniteQueryResult<{ pages: DbtJobHistoryPage[]; pageParams: (string | null)[] }, Error> {
  return useInfiniteQuery<
    DbtJobHistoryPage,
    Error,
    { pages: DbtJobHistoryPage[]; pageParams: (string | null)[] }
  >({
    ...listQueryOptions,
    queryKey: dbtJobHistoryKey(id, options.include),
    enabled: options.enabled,
    initialPageParam: null,
    queryFn: async ({ pageParam, signal }) => {
      const envelope = await apiRequestEnvelope<DbtJobHistory>(
        dbtJobHistoryPath(id, {
          limit: DBT_JOB_HISTORY_PAGE_SIZE,
          cursor: (pageParam as string | null) ?? null,
          include: options.include,
        }),
        { signal },
      );
      return {
        data: envelope.data as DbtJobHistory,
        nextCursor: envelope.meta?.nextCursor ?? null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  });
}

/**
 * Marks one transformation's history stale (every `include`), so a step that
 * was in flight shows its outcome instead of "Running" once it lands. An open
 * History tab refetches at once; a closed one on its next mount.
 */
export function useRefreshDbtJobHistory(): (id: string) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    (id: string) =>
      queryClient.invalidateQueries({ queryKey: [dbtJobHistoryKey(id, undefined)[0]] }),
    [queryClient],
  );
}

/** Invalidate the list, the graph, the recent steps and, when named, one row and its history. */
function useDbtJobInvalidation(): (id?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (id?: string) => {
    await queryClient.invalidateQueries({ queryKey: [DBT_JOBS_PATH] });
    await queryClient.invalidateQueries({ queryKey: [DBT_JOBS_GRAPH_PATH] });
    await queryClient.invalidateQueries({ queryKey: [DBT_JOBS_RUNS_PATH] });
    // Every transformation's history: a new run is a new column for each, and a new
    // selector changes which models are rows.
    await queryClient.invalidateQueries({ predicate: isDbtJobHistoryQuery });
    if (id) await queryClient.invalidateQueries({ queryKey: [dbtJobPath(id)] });
  };
}

function isDbtJobHistoryQuery(query: { queryKey: readonly unknown[] }): boolean {
  const head = query.queryKey[0];
  return (
    typeof head === "string" && head.startsWith(`${DBT_JOBS_PATH}/`) && head.endsWith("/history")
  );
}

export function useCreateDbtJob(): UseMutationResult<DbtJobView, unknown, CreateDbtJobBody> {
  const invalidate = useDbtJobInvalidation();
  return useMutation({
    mutationFn: (body: CreateDbtJobBody) => createDbtJob(body),
    onSuccess: (view) => invalidate(view.id),
  });
}

export function useUpdateDbtJob(): UseMutationResult<
  DbtJobView,
  unknown,
  { id: string; body: UpdateDbtJobBody }
> {
  const invalidate = useDbtJobInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDbtJobBody }) => updateDbtJob(id, body),
    onSuccess: (view) => invalidate(view.id),
  });
}

export function useDeleteDbtJob(): UseMutationResult<void, unknown, string> {
  const invalidate = useDbtJobInvalidation();
  return useMutation({
    mutationFn: (id: string) => deleteDbtJob(id),
    onSuccess: (_result, id) => invalidate(id),
  });
}

/**
 * `POST /transformations/run`. The caller supplies the Idempotency-Key: the
 * dialog mints one per open and replays it on retry (API-STANDARD.md §6).
 *
 * The run history is invalidated too: the new run is a row on the Runs screen
 * as well as a transform step here.
 */
export function useStartTransformRun(): UseMutationResult<
  StartTransformRunResponse,
  unknown,
  { body: RunDbtJobsBody; idempotencyKey: string }
> {
  const invalidate = useDbtJobInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: RunDbtJobsBody; idempotencyKey: string }) =>
      startTransformRun(body, idempotencyKey),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: [RUNS_PATH] });
    },
  });
}
